import { queryOptions, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import apiClient from './client';
import { API } from './endpoints';
import { useNotificationStore } from '../stores/notificationStore';
import { useGlobalStore } from '../stores/globalStore';
import { trayLineKeys } from './trayLineApi';
import { foodAllergyKeys } from './foodAllergyApi';
import type {
  Patient,
  GuestPatient,
  ResidentFlagResult,
  ResidentUpdateCheck,
  DietFluc,
  GuestSchedule,
  DietType,
  Consistency,
  FoodAllergy,
  Doctor,
  UnitMapping,
  UnitRoomMapping,
  SpecialNeedRef,
  PatientStatusHistory,
  LogInteraction,
  PatientAttendance,
  PatientAttendanceExtended,
  PatientTrayLine,
  PatientTrayLineDefaults,
  PatientTrayLineOverrideRow,
  PatientTrayLineEnvelope,
  PatientTrayLineAssignmentRow,
  PatientDeletedSummary,
} from '../types/patient';
import type { SpecialDevice } from '../types/referenceData';

// --- Query Keys ---

export const residentKeys = {
  all: ['residents'] as const,
  list: (facilityId: number) => [...residentKeys.all, 'list', facilityId] as const,
  detail: (facilityId: number, patientId: number) =>
    [...residentKeys.all, 'detail', facilityId, patientId] as const,
  flags: (facilityId: number) => [...residentKeys.all, 'flags', facilityId] as const,
  updateCheck: (facilityId: number) => [...residentKeys.all, 'updateCheck', facilityId] as const,
  dietFlucs: (facilityId: number) => [...residentKeys.all, 'dietFlucs', facilityId] as const,
  guests: (facilityId: number) => [...residentKeys.all, 'guests', facilityId] as const,
  guestsByPatient: (facilityId: number, patientId: number) =>
    [...residentKeys.all, 'guestsByPatient', facilityId, patientId] as const,
  statusHistory: (facilityId: number, patientId: number) =>
    [...residentKeys.all, 'statusHistory', facilityId, patientId] as const,
  refData: (facilityId: number) => [...residentKeys.all, 'refData', facilityId] as const,
  dietHistory: (facilityId: number, patientId: number) =>
    [...residentKeys.all, 'dietHistory', facilityId, patientId] as const,
  interactionLogs: (facilityId: number, patientId: number) =>
    [...residentKeys.all, 'interactionLogs', facilityId, patientId] as const,
  systemLogs: (facilityId: number, startDate: string, endDate: string) =>
    [...residentKeys.all, 'systemLogs', facilityId, startDate, endDate] as const,
  deletedResidentsLogs: (facilityId: number) =>
    [...residentKeys.all, 'deletedResidentsLogs', facilityId] as const,
  deleted: (facilityId: number) => [...residentKeys.all, 'deleted', facilityId] as const,
  deletedSummary: (facilityId: number) => [...residentKeys.all, 'deletedSummary', facilityId] as const,
  attendance: (facilityId: number, patientId: number) =>
    [...residentKeys.all, 'attendance', facilityId, patientId] as const,
  attendanceExtended: (facilityId: number, patientId: number) =>
    [...residentKeys.all, 'attendanceExtended', facilityId, patientId] as const,
  trayLine: (facilityId: number, patientId: number) =>
    [...residentKeys.all, 'trayLine', facilityId, patientId] as const,
  trayLineEnvelope: (facilityId: number, patientId: number) =>
    [...residentKeys.all, 'trayLineEnvelope', facilityId, patientId] as const,
};

// --- Fetch Helpers ---
// Errors are reported via the notification store so users see feedback.

async function fetchSafe<T>(url: string): Promise<T[]> {
  try {
    const { data } = await apiClient.get<T[]>(url);
    return data ?? [];
  } catch (err) {
    const endpoint = url.split('/').pop()?.split('?')[0] ?? url;
    const status = (err as { response?: { status?: number } })?.response?.status;
    const detail = status ? ` (HTTP ${status})` : '';
    console.error(`[fetchSafe] Failed to load ${url}${detail}`, err);
    useNotificationStore.getState().addNotification(
      `Failed to load ${endpoint}${detail}`,
      'error',
    );
    return [];
  }
}

// --- List & Detail Queries ---

export function useResidents(facilityId: number) {
  return useQuery({
    queryKey: residentKeys.list(facilityId),
    queryFn: () => fetchSafe<Patient>(API.patients(facilityId)),
    enabled: facilityId > 0,
    staleTime: 30 * 1000,
  });
}

/**
 * Returns the FULL `Patient` records eligible for the Posting
 * Selective Menu report over `[startDate, endDate]` given a bit-flag
 * `mealtimes`. Mirrors WinForms `Fill_dtResidents` at
 * `ucDietech_Report_Generator_Posting_SelectiveMenu.vb:508` which
 * calls `GetSelectiveMenuResidents(cnn, fac, startDate, endDate,
 * meals, false)` and binds the returned DataTable directly to the
 * picker grid.
 *
 * Returns full patients (regulars + GUESTS with `fKey_Patient !=
 * null`) so the picker doesn't have to intersect with
 * `useResidents(fac)` (which excludes guests via
 * `ResidentManager` at `ResidentManager.cs:155`).
 *
 * Auto-disabled until all inputs are present. `startIso` / `endIso`
 * must be `yyyy-MM-dd` strings and `mealtimes` must be > 0.
 */
export function useSelectiveMenuEligiblePatients(
  facilityId: number,
  startIso: string | null,
  endIso: string | null,
  mealtimes: number,
) {
  return useQuery<Patient[]>({
    queryKey: ['selectiveMenuEligiblePatients', facilityId, startIso, endIso, mealtimes] as const,
    queryFn: async () => {
      const { data } = await apiClient.post<Patient[]>(
        `/api/Patient/SelectiveMenuEligible/${facilityId}`,
        {
          StartDate: startIso,
          EndDate: endIso,
          Mealtimes: mealtimes,
        },
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && !!startIso && !!endIso && mealtimes > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * Returns the FULL `Patient` records eligible for the Posting -
 * Specific Resident report on `startDate`. Returns all ACTIVE
 * regulars + guests (rows with `fKey_Patient != null`) — the grid
 * is NOT filtered by selected mealtimes (per user directive; see
 * `PatientDAL.GetPostingSpecificResidentEligiblePatients` summary
 * for the WinForms alignment rationale).
 *
 * Guests are post-filtered by `ValidMealtimesForGuestRecurrence`
 * (`modCoreUtils.vb:108-163`) for `startDate` so recurrence
 * scheduling drives visibility on that day.
 *
 * `mealtimes` is accepted for API stability but ignored by the
 * endpoint — kept out of the query key so mealtime toggles don't
 * cause needless refetches.
 */
/**
 * Returns the FULL `Patient` records eligible for the Tray Tickets
 * picker grid across a date range. Regulars + guests whose
 * recurrence matches ANY day in `[startIso, startIso + numberOfDays - 1]`.
 * Mirrors WinForms `Fill_dtPatientGrid` at
 * `ucDietech_Report_Generator_TrayTicket.vb:1010-1122`.
 */
/** One row in the Weight - Trigger picker grid. Mirrors the WinForms
 *  `_dsWeight_Trigger.Tables("tblResidents")` shape. */
export interface WeightTriggerCandidate {
  pKey_Patient: number;
  fullName: string;
  weight: number;
  details: WeightTriggerDetail[];
}
export interface WeightTriggerDetail {
  sort: number;
  days: string;
  wgtGain: string;
  wgtGain_DateTime: string | null;
  wgtLoss: string;
  wgtLoss_DateTime: string | null;
}

/** Returns residents whose weight has triggered per the shared
 *  `Diet_Utils.WeightCheckForTriggers` helper. Server-computed —
 *  the master-detail grid on Weight - Trigger renders these rows
 *  read-only and derives `LstPatient` on submit from the returned
 *  PKey_Patient list. Mirrors WinForms `Fill_dtWeight_Trigger` at
 *  `ucDietech_Report_Generator_WeightTrigger.vb:327-414`. */
export function useWeightTriggerCandidates(facilityId: number) {
  return useQuery<WeightTriggerCandidate[]>({
    queryKey: ['weightTriggerCandidates', facilityId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<WeightTriggerCandidate[]>(
        `/api/Patient/WeightTriggerCandidates/${facilityId}`,
      );
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * The residents who have a saved tray ticket on one date for any of the given
 * mealtimes — what Past Tray Tickets picks from, rather than every resident in
 * the facility (src/docs/migrations/PastTrayTickets-migration.md).
 *
 * Read from the census rows, so a resident who has since moved room or been
 * discharged still appears, with the location their ticket printed with.
 */
export function usePastTrayTicketEligiblePatients(
  facilityId: number,
  dateIso: string | null,
  mealtimes: number[],
) {
  // Sorted and joined so the key is stable whatever order the checkboxes were
  // ticked in — otherwise every re-tick refetches.
  const mealKey = [...mealtimes].sort((a, b) => a - b).join(',');

  return useQuery<Patient[]>({
    queryKey: ['pastTrayTicketEligiblePatients', facilityId, dateIso, mealKey] as const,
    queryFn: async () => {
      const { data } = await apiClient.post<Patient[]>(
        `/api/Patient/PastTrayTicketEligible/${facilityId}`,
        { DateFor: dateIso, Mealtimes: mealtimes },
      );
      return data ?? [];
    },
    // No mealtimes means nothing to show, so do not ask the server.
    enabled: facilityId > 0 && !!dateIso && mealtimes.length > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * The residents who have SAVED nourishment labels on one date for any of the
 * given mealtimes — what Past Nourishment Labels picks from, rather than every
 * resident in the facility (src/docs/migrations/PastTrayTickets-migration.md,
 * Nourishment Labels section).
 *
 * Read from the census rows, so a resident who has since moved room or been
 * discharged still appears, with the location their labels printed with.
 *
 * Separate from usePastTrayTicketEligiblePatients because the two read different
 * census rows: this one sees the nourishment pass
 * (Menu_Header_ForeignKeyLink = -999999), that one the tray ticket pass.
 */
export function usePastNourishmentLabelEligiblePatients(
  facilityId: number,
  dateIso: string | null,
  mealtimes: number[],
) {
  // Sorted and joined so the key is stable whatever order the checkboxes were
  // ticked in — otherwise every re-tick refetches.
  const mealKey = [...mealtimes].sort((a, b) => a - b).join(',');

  return useQuery<Patient[]>({
    queryKey: ['pastNourishmentLabelEligiblePatients', facilityId, dateIso, mealKey] as const,
    queryFn: async () => {
      const { data } = await apiClient.post<Patient[]>(
        `/api/Patient/PastNourishmentLabelEligible/${facilityId}`,
        { DateFor: dateIso, Mealtimes: mealtimes },
      );
      return data ?? [];
    },
    // No mealtimes means nothing to show, so do not ask the server.
    enabled: facilityId > 0 && !!dateIso && mealtimes.length > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * The distinct label types saved for one date and set of mealtimes.
 *
 * Past Nourishment Labels needs this in `preSubmit`, before it shows the label
 * selector: whether to show it at all (only a sheet label — type 0 or 1 — has
 * positions to leave blank) and which variant to show. Both must follow the
 * label the reprint will render, NOT the facility's current
 * `nourishment_Labels_Type` — a facility that has since changed label stock
 * would otherwise be offered the wrong sheet.
 *
 * A plain fetch rather than a hook: `preSubmit` is an async callback, not a
 * render, so there is no hook to call there.
 */
export async function fetchPastNourishmentLabelTypes(
  facilityId: number,
  dateIso: string | null,
  mealtimes: number[],
): Promise<PastNourishmentLabelType[]> {
  if (facilityId <= 0 || !dateIso || mealtimes.length === 0) return [];

  const { data } = await apiClient.post<PastNourishmentLabelType[]>(
    `/api/Patient/PastNourishmentLabelTypes/${facilityId}`,
    { DateFor: dateIso, Mealtimes: mealtimes },
  );

  return data ?? [];
}

/** One mealtime and the label type its saved labels printed on. Per mealtime
 *  rather than a distinct set, because a date CAN span two label stocks and the
 *  form has to be able to say which mealtimes go together. */
export interface PastNourishmentLabelType {
  mealtime: number;
  labelType: number;
}

/** Label types whose layout honours `LabelsToSkip` (`BlankDetailCount`). Types 2
 *  and 3 are single/roll labels and hardcode 0 with a custom page size, so
 *  skipping positions has nothing to act on. */
export const SHEET_LABEL_TYPES = [0, 1];

export interface PastSnapshotDateRange {
  minDate: string | null;
  maxDate: string | null;
}

/**
 * The first and last dates that have saved snapshots, so a past-report date
 * picker can be clamped to days that actually have something to reprint.
 *
 * How far back it reaches is the facility's own `Census_AutoDeleteDays`
 * retention, so it is read rather than assumed, and it moves as old rows age
 * out. The two report kinds are counted separately — a day with tray tickets
 * but no nourishment labels is outside the labels' range.
 */
export function usePastSnapshotDateRange(facilityId: number, nourishmentLabels: boolean) {
  return useQuery<PastSnapshotDateRange>({
    queryKey: ['pastSnapshotDateRange', facilityId, nourishmentLabels] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<PastSnapshotDateRange>(
        `/api/Patient/PastSnapshotDateRange/${facilityId}`,
        { params: { nourishmentLabels } },
      );
      return data ?? { minDate: null, maxDate: null };
    },
    enabled: facilityId > 0,
    staleTime: 60 * 1000,
  });
}

export function useTrayTicketEligiblePatients(
  facilityId: number,
  startIso: string | null,
  numberOfDays: number,
) {
  const days = Math.max(1, numberOfDays);
  return useQuery<Patient[]>({
    queryKey: ['trayTicketEligiblePatients', facilityId, startIso, days] as const,
    queryFn: async () => {
      const { data } = await apiClient.post<Patient[]>(
        `/api/Patient/TrayTicketEligible/${facilityId}`,
        { StartDate: startIso, NumberOfDays: days },
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && !!startIso,
    staleTime: 60 * 1000,
  });
}

/**
 * The Med Pass - Summary resident grid: residents AND guests (bulk-order guests
 * too, no guest-schedule check), never bulk-order residents. WinForms
 * `Fill_dtPatientGrid` (ucDietech_Report_Generator_MedPassSummary.vb:503-547);
 * Specific Resident picks from it and Specific Floor/Unit walks it (.vb:223-260).
 */
export const medPassSummaryPatientsKey = (facilityId: number) =>
  ['medPassSummaryPatients', facilityId] as const;

export async function fetchMedPassSummaryPatients(facilityId: number): Promise<Patient[]> {
  const { data } = await apiClient.get<Patient[]>(`/api/Patient/MedPassSummaryGrid/${facilityId}`);
  return data ?? [];
}

export function useMedPassSummaryPatients(facilityId: number, enabled = true) {
  return useQuery<Patient[]>({
    queryKey: medPassSummaryPatientsKey(facilityId),
    queryFn: () => fetchMedPassSummaryPatients(facilityId),
    enabled: enabled && facilityId > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * Posting - Specific Resident's resident list for the start date. WinForms
 * fills it once, in Init, before any mealtime is checked (Fill_dtResidents,
 * ucDietech_Report_Generator_Posting_SpecificResident.vb:155, :490-530), so
 * it has no mealtime attendance filter (Mealtimes 0) and checking a mealtime
 * does not change it.
 */
export function usePostingSpecificResidentEligiblePatients(
  facilityId: number,
  startIso: string | null,
) {
  return useQuery<Patient[]>({
    queryKey: ['postingSpecificResidentEligiblePatients', facilityId, startIso] as const,
    queryFn: async () => {
      const { data } = await apiClient.post<Patient[]>(
        `/api/Patient/PostingSpecificResidentEligible/${facilityId}`,
        {
          StartDate: startIso,
          Mealtimes: 0,
        },
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && !!startIso,
    staleTime: 60 * 1000,
  });
}

/** Row returned by the Patient Labels report's grid endpoint —
 *  mirrors the C# `PatientDAL.PatientLabelsGridRow` shape.
 *  Includes both regular patients (`fKey_Patient == null`) and
 *  guests (`fKey_Patient != null`) with all 21 `attend_Day_*`
 *  attendance flags plus a joined `unit` label. */
export interface PatientLabelsGridRow {
  pKey_Patient: number;
  fullName: string;
  fKey_Patient: number | null;
  floor: string | null;
  wing: string | null;
  room_No: number | null;
  bed_No: string | null;
  unit: string | null;
  dietOrder: string;
  attend_Day_B1: number; attend_Day_B2: number; attend_Day_B3: number;
  attend_Day_B4: number; attend_Day_B5: number; attend_Day_B6: number; attend_Day_B7: number;
  attend_Day_L1: number; attend_Day_L2: number; attend_Day_L3: number;
  attend_Day_L4: number; attend_Day_L5: number; attend_Day_L6: number; attend_Day_L7: number;
  attend_Day_D1: number; attend_Day_D2: number; attend_Day_D3: number;
  attend_Day_D4: number; attend_Day_D5: number; attend_Day_D6: number; attend_Day_D7: number;
  /** Guest-only recurrence check for the report date. Regular
   *  patients get `true` server-side so the client can filter
   *  without branching on `fKey_Patient`. Guests with `false`
   *  are already excluded from the response (WinForms drops
   *  them in `Fill_dtPatientGrid` at .vb:384), but the field
   *  is kept for symmetry / debugging. */
  guestValidForDate: boolean;
  /** Guest-only mealtime bools from the schedule row that
   *  covered the report date. Regular patients get `true` on
   *  all three so client-side filters short-circuit. */
  guestMealtimeB: boolean;
  guestMealtimeL: boolean;
  guestMealtimeD: boolean;
}

/** Backs the Patient Labels report grid. Returns patients +
 *  guests with per-day attendance flags and joined Unit label —
 *  parity with WinForms `Fill_dtPatientGrid` at
 *  `ucDietech_Report_Generator_Resident_Labels.vb:307-318`.
 *
 *  `dateIso` drives the server-side guest-recurrence filter
 *  (mirrors WinForms `ValidMealtimesForGuestRecurrence` at
 *  modCoreUtils.vb:108-163). Guests whose schedule doesn't
 *  cover the date are omitted from the response entirely.
 *  Query key includes the date so React Query refetches on
 *  Date field changes. */
export function usePatientLabelsGrid(facilityId: number, dateIso?: string) {
  return useQuery({
    queryKey: [...residentKeys.all, 'labelsGrid', facilityId, dateIso ?? ''] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<PatientLabelsGridRow[]>(
        API.patientLabelsGrid(facilityId, dateIso),
      );
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 30 * 1000,
  });
}

export function useResident(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: residentKeys.detail(facilityId, patientId),
    queryFn: async (): Promise<Patient | null> => {
      try {
        const { data } = await apiClient.get<Patient>(API.patientById(facilityId, patientId));
        return data;
      } catch {
        return null;
      }
    },
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * The next Res_ID for a new resident (facility AutoIncrementResidentID).
 * Null when the facility has none to offer or the call fails: the field
 * then starts empty and the user types one, as before.
 */
export function useNextResId(facilityId: number, enabled: boolean) {
  return useQuery({
    queryKey: [...residentKeys.all, 'nextResId', facilityId] as const,
    queryFn: async (): Promise<string | null> => {
      try {
        const { data } = await apiClient.get<{ resId?: string | null }>(API.patientNextResId(facilityId));
        const id = typeof data?.resId === 'string' ? data.resId.trim() : '';
        return id || null;
      } catch {
        return null;
      }
    },
    enabled: enabled && facilityId > 0,
    // Another user may take the number; fetch fresh for each new resident.
    staleTime: 0,
    gcTime: 0,
  });
}

// --- Flags ---

export function useResidentFlags(facilityId: number) {
  return useQuery({
    queryKey: residentKeys.flags(facilityId),
    queryFn: () => fetchSafe<ResidentFlagResult>(API.patientFlags(facilityId)),
    enabled: facilityId > 0,
    staleTime: 30 * 1000,
  });
}

// --- Auto-Refresh (lightweight polling) ---

export function useResidentAutoRefresh(facilityId: number, intervalMs = 60000) {
  const queryClient = useQueryClient();
  const lastCheckRef = useRef<{ count: number; lastModified: string | null }>({
    count: -1,
    lastModified: null,
  });

  const { data: checkData } = useQuery({
    queryKey: residentKeys.updateCheck(facilityId),
    queryFn: async (): Promise<ResidentUpdateCheck | null> => {
      try {
        const { data } = await apiClient.get<ResidentUpdateCheck>(
          API.patientUpdateCheck(facilityId)
        );
        return data;
      } catch {
        return null;
      }
    },
    enabled: facilityId > 0,
    refetchInterval: intervalMs,
    staleTime: 0,
  });

  useEffect(() => {
    if (!checkData) return;
    const prev = lastCheckRef.current;
    const changed =
      prev.count >= 0 &&
      (prev.count !== checkData.count || prev.lastModified !== checkData.lastModified);

    lastCheckRef.current = { count: checkData.count, lastModified: checkData.lastModified };

    if (changed) {
      queryClient.invalidateQueries({ queryKey: residentKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.flags(facilityId) });
    }
  }, [checkData, facilityId, queryClient]);
}

// --- Diet Flucs ---

export function useDietFlucs(facilityId: number) {
  return useQuery({
    queryKey: residentKeys.dietFlucs(facilityId),
    queryFn: () => fetchSafe<DietFluc>(API.dietFlucsActive(facilityId)),
    enabled: facilityId > 0,
    staleTime: 60 * 1000,
  });
}

/**
 * Each resident's current diet order (the desktop's DT_ResidentDietFlucs),
 * for a one-off read such as a report's Select Active. Unlike `useDietFlucs`
 * a failed request throws: fetchSafe's empty list would read as "no resident
 * is on any diet". Keyed under `dietFlucs`, so saving a diet order marks it
 * stale as well.
 */
export function currentDietOrdersQuery(facilityId: number) {
  return queryOptions({
    queryKey: [...residentKeys.dietFlucs(facilityId), 'current'] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<DietFluc[]>(API.dietFlucsActive(facilityId));
      return data ?? [];
    },
    staleTime: 60 * 1000,
  });
}

/** Per-unit room+bed catalog from `tblUnit_Room_Mapping`. Drives
 *  the Room/Bed dropdown mode on the Primary Info Location
 *  fieldset (gated by either facility `ForceRoomBedSelection`
 *  or unit `UseRoomTray`). Disabled unless `enabled` AND the
 *  unit is selected so we don't fire a fetch in free-text mode. */
export function useUnitRooms(unitId: number, enabled: boolean) {
  return useQuery({
    queryKey: ['unitRooms', unitId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<UnitRoomMapping[]>(API.facilityUnitRooms(unitId));
      return data ?? [];
    },
    enabled: enabled && unitId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Pulls the EARLIEST scheduled future fluc per patient. Drives
 * the "(Future Diet … On …)" suffix on the Diet Order grid
 * cell. Server-side filters Change_Date &gt; now AND clips
 * to the requested facility, so this list is one row per
 * patient (or empty if no future change is scheduled).
 */
export function useFutureDietFlucs(facilityId: number) {
  return useQuery({
    queryKey: [...residentKeys.all, 'dietFlucsFuture', facilityId] as const,
    queryFn: () => fetchSafe<DietFluc>(API.dietFlucsFuture(facilityId)),
    enabled: facilityId > 0,
    staleTime: 60 * 1000,
  });
}

// --- Guest Schedules ---

export interface PatientGuestsOptions {
  /** The client's today (YYYY-MM-DD): sets each guest's mealB/L/D. */
  date?: string;
  /** Also list discharged guests (deleted ones never are). */
  includeDischarged?: boolean;
}

export function usePatientGuests(facilityId: number, options: PatientGuestsOptions = {}) {
  const { date, includeDischarged = false } = options;
  return useQuery({
    // Params go after facilityId so invalidating
    // [...residentKeys.all, 'patientGuests', facilityId] refreshes every variant.
    queryKey: date || includeDischarged
      ? [...residentKeys.all, 'patientGuests', facilityId, { date: date ?? null, includeDischarged }]
      : [...residentKeys.all, 'patientGuests', facilityId],
    queryFn: () => fetchSafe<GuestPatient>(API.patientGuests(facilityId, { date, includeDischarged })),
    enabled: facilityId > 0,
    staleTime: 60 * 1000,
  });
}

export function useGuestSchedules(facilityId: number) {
  return useQuery({
    queryKey: residentKeys.guests(facilityId),
    queryFn: () => fetchSafe<GuestSchedule>(API.guestSchedules(facilityId)),
    enabled: facilityId > 0,
    staleTime: 60 * 1000,
  });
}

export function useGuestsByPatient(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: residentKeys.guestsByPatient(facilityId, patientId),
    queryFn: () =>
      fetchSafe<GuestSchedule>(API.guestSchedulesByPatient(facilityId, patientId)),
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 60 * 1000,
  });
}

// Full TblPatient_GuestSchedule rows for a given guest's PKey.
// Used by GuestProfile to populate the Schedule tab on load —
// the GuestSchedule summary type that `useGuestsByPatient`
// returns drops the recurrence-detail fields we need to
// rehydrate the form (RecurrencePattern, weekday booleans,
// RecurrenceStarts, RecurrenceEndsBy, etc.). Hits the actual
// controller route at `ByPatient/{f}/{p}`, not the
// legacy-mismatch URL above.
export interface GuestScheduleRow {
  pKey_Patient_GuestSchedule: number;
  fKey_Patient: number;
  // tblPatient_GuestSchedule's mealtime columns serialize from
  // the C# DTO as `breakfast` / `lunch` / `dinner` — NOT
  // `mealtime_B/L/D`. (The underlying SQL columns ARE named
  // Mealtime_B/L/D; the DTO uses the friendlier property
  // names.) Matching the wire names here so the seed effect
  // reads non-undefined booleans.
  breakfast: boolean;
  lunch: boolean;
  dinner: boolean;
  specificDate: string | null;
  isRecurrence: boolean;
  recurrencePattern: number | null;
  recurrenceOccursEveryNPattern: number | null;
  recurrenceEveryWeekday: boolean;
  recurrenceDay1: boolean;
  recurrenceDay2: boolean;
  recurrenceDay3: boolean;
  recurrenceDay4: boolean;
  recurrenceDay5: boolean;
  recurrenceDay6: boolean;
  recurrenceDay7: boolean;
  recurrenceStarts: string | null;
  recurrenceEndsBy: string | null;
}

export function useGuestScheduleForGuest(facilityId: number, guestPatientId: number) {
  return useQuery({
    queryKey: [...residentKeys.all, 'guestScheduleForGuest', facilityId, guestPatientId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<GuestScheduleRow[]>(
        API.guestScheduleByGuestPatient(facilityId, guestPatientId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && guestPatientId > 0,
    staleTime: 30 * 1000,
  });
}

// --- Status History ---

export function usePatientStatusHistory(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: residentKeys.statusHistory(facilityId, patientId),
    queryFn: () =>
      fetchSafe<PatientStatusHistory>(API.patientStatusHistory(facilityId, patientId)),
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

// Delete a single status-history row by PKey. Invalidates the
// patient's status-history query on success so the grid
// refreshes automatically.
export function useDeletePatientStatusHistory(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (pKeyStatusHistory: number) => {
      await apiClient.delete(API.patientStatusHistoryDelete(facilityId, pKeyStatusHistory, patientId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: residentKeys.statusHistory(facilityId, patientId),
      });
    },
  });
}

// Update the DateTimeOfLog on a status-history row by PKey.
// Body is a local-wallclock ISO string (no Z) to keep the date
// the user picked from drifting across timezones.
export function useUpdatePatientStatusHistoryDateTime(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { pKeyStatusHistory: number; dateTimeOfLog: string }) => {
      await apiClient.put(
        API.patientStatusHistoryUpdate(facilityId, vars.pKeyStatusHistory, patientId),
        { dateTimeOfLog: vars.dateTimeOfLog },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: residentKeys.statusHistory(facilityId, patientId),
      });
    },
  });
}

// Update or clear the patient's photo on tblPatient.Image.
// Pass an empty string to clear. The body is a base64 string
// (data-URL prefix is OK — the server strips it). Returns the
// fresh data-URL the avatar should render. Intentionally NOT
// invalidating the full patient query — the caller updates
// `form.imagePath` from the response so a refetch isn't
// needed (saves a round-trip with a big base64 payload).
export function useUpdatePatientImage(facilityId: number, patientId: number) {
  return useMutation({
    mutationFn: async (imageBase64: string | null): Promise<string | null> => {
      const { data } = await apiClient.put<{ imagePath: string | null }>(
        API.patientImage(facilityId, patientId),
        { imageBase64: imageBase64 ?? '' },
      );
      return data?.imagePath ?? null;
    },
  });
}

// Payload for the patient Calorie / Protein / Fluid Needs
// save — column set mirrors the WinForms
// `ResdientCalorieProteinFluidCalculations.Update_dtPatient`
// UPDATE so a save from React writes exactly the same row
// the legacy app does.
export interface CalProteinFluidNeedsPayload {
  a_Factor: number | null;
  i_Factor: number | null;
  protein_Condition: number;
  protein_Exceptions: number;
  needsCalorieWeightToUse: number;
  needsProteinWeightToUse: number;
  needsFluidsWeightToUse: number;
  mealPlanProvidesCalories: string | null;
  mealPlanProvidesProtein: string | null;
  mealPlanProvidesFluids: string | null;
  fluid_Condition: number;
  fluid_Condition_Other: string | null;
  fluid_Condition_FluidLow: number | null;
  fluid_Condition_FluidHigh: number | null;
  protein_Condition_Other: string | null;
  protein_Condition_AlbuminLow: number | null;
  protein_Condition_AlbuminHigh: number | null;
  protein_Condition_ProteinLow: number | null;
  protein_Condition_ProteinHigh: number | null;
  calorie_RangeLow: number | null;
  calorie_RangeHigh: number | null;
  calorie_Other: string | null;
  calorie_EnergyPerKilogramRange: number;
  calorie_EnergyCalculation: number;
  iBW_Start: number | null;
  iBW_End: number | null;
}

// Save mutation for the Calorie/Protein tab. Posts the payload
// + signed-in user identity so the server-side DAL writes the
// audit log (Location = CalorieProteinFluidNeeds, Type = Modify)
// in the same call — the React side does NOT call addLog
// separately for this. Invalidates the patient detail cache on
// success so the computed display picks up the new values.
export function useUpdateCalProteinFluidNeeds(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CalProteinFluidNeedsPayload) => {
      const { userId, userName, userFirstName, userLastName } = useGlobalStore.getState();
      await apiClient.post(API.patientCalProteinFluidNeeds(facilityId, patientId), {
        data,
        pmSec_User_PKey_User: userId || null,
        pmSec_User_UserName: userName || null,
        pmSec_User_FirstName: userFirstName || null,
        pmSec_User_LastName: userLastName || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: ['patient', facilityId, patientId] });
    },
  });
}

// --- Reference Data (parallel fetch) ---

export interface ReferenceData {
  dietTypes: DietType[];
  consistencies: Consistency[];
  foodAllergies: FoodAllergy[];
  doctors: Doctor[];
  units: UnitMapping[];
  specialDevices: SpecialDevice[];
  specialNeeds: SpecialNeedRef[];
}

/** The query function behind useReferenceData, for callers that read another
 *  facility's reference data (e.g. its units) through the same cache entry. */
export async function fetchReferenceData(facilityId: number): Promise<ReferenceData> {
  // Use direct API calls so failures propagate to React Query for retry
  // instead of being silently cached as empty arrays by fetchSafe.
  const fetchRef = async <T>(url: string, label: string): Promise<T[]> => {
    try {
      const { data } = await apiClient.get<T[]>(url);
      return data ?? [];
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      console.warn(`[RefData] Failed to load ${label} from ${url} (HTTP ${status ?? '?'})`);
      return [];
    }
  };

  const [dietTypes, consistencies, foodAllergies, doctors, units, specialDevices, specialNeeds] =
    await Promise.all([
      fetchRef<DietType>(API.dietTypes(facilityId), 'dietTypes'),
      fetchRef<Consistency>(API.consistencies(facilityId), 'consistencies'),
      fetchRef<FoodAllergy>(API.foodAllergies(facilityId), 'foodAllergies'),
      fetchRef<Doctor>(API.doctors(facilityId), 'doctors'),
      fetchRef<UnitMapping>(API.units(facilityId), 'units'),
      fetchRef<SpecialDevice>(API.specialDevices(facilityId), 'specialDevices'),
      fetchRef<SpecialNeedRef>(API.specialNeeds(facilityId), 'specialNeeds'),
    ]);

  // If ALL critical ref data is empty, the API is likely unreachable — throw so React Query retries
  if (dietTypes.length === 0 && consistencies.length === 0 && doctors.length === 0) {
    throw new Error('Reference data endpoints returned no data - API may be unreachable');
  }

  return { dietTypes, consistencies, foodAllergies, doctors, units, specialDevices, specialNeeds };
}

export function useReferenceData(facilityId: number) {
  return useQuery({
    queryKey: residentKeys.refData(facilityId),
    queryFn: () => fetchReferenceData(facilityId),
    enabled: facilityId > 0,
    staleTime: 5 * 60 * 1000,
    retry: 3,
  });
}

// --- Diet History (per patient) ---

export function useDietHistory(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: residentKeys.dietHistory(facilityId, patientId),
    queryFn: () =>
      fetchSafe<DietFluc>(API.dietFlucsPatientHistory(facilityId, patientId)),
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

// --- DRI Groups (lookup table for nutritional reference groups) ---

export interface DRIGroup {
  pKey_RC_DRI_Group: number;
  name: string | null;
  /** tblRC_DRI_Group.Gender: 0 female, 1 male, 2 either. */
  gender?: number | null;
}

export function useDRIGroups(facilityId: number) {
  return useQuery({
    queryKey: ['driGroups', facilityId],
    queryFn: async (): Promise<DRIGroup[]> => {
      const { data } = await apiClient.get<DRIGroup[]>(API.driGroups(facilityId));
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 10 * 60 * 1000,
  });
}

// --- Patient nutritional needs (calorie/protein/fluid ranges) ---

export interface PatientNutritionalNeeds {
  caloriesLow: number;
  caloriesHigh: number;
  proteinLow: number;
  proteinHigh: number;
  fluidLow: number;
  fluidHigh: number;
  averageCalories: number;
  averageProtein: number;
  averageFluid: number;
}

export function usePatientNutritionalNeeds(facilityId: number, patientId: number, gender: string | null | undefined) {
  return useQuery({
    queryKey: ['patientNutritionalNeeds', facilityId, patientId, gender ?? ''],
    queryFn: async (): Promise<PatientNutritionalNeeds> => {
      const { data } = await apiClient.get<PatientNutritionalNeeds>(
        API.patientNutritionalNeeds(facilityId, patientId, gender ?? 'U'),
      );
      return data ?? {
        caloriesLow: 0, caloriesHigh: 0,
        proteinLow: 0, proteinHigh: 0,
        fluidLow: 0, fluidHigh: 0,
        averageCalories: 0, averageProtein: 0, averageFluid: 0,
      };
    },
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Patient field MRU (most-recently-used free-text values per facility) ---

export interface PatientMRU {
  meal_Acpt: string[];
  sup_Acpt: string[];
  fluid_Acpt: string[];
  advanceDirective: string[];
  religious_Ethnic: string[];
  drugAllergies: string[];
}

const EMPTY_PATIENT_MRU: PatientMRU = {
  meal_Acpt: [],
  sup_Acpt: [],
  fluid_Acpt: [],
  advanceDirective: [],
  religious_Ethnic: [],
  drugAllergies: [],
};

export function usePatientMRU(facilityId: number) {
  return useQuery({
    queryKey: ['patientMRU', facilityId],
    queryFn: async (): Promise<PatientMRU> => {
      const { data } = await apiClient.get<PatientMRU>(API.patientMRU(facilityId));
      return data ?? EMPTY_PATIENT_MRU;
    },
    enabled: facilityId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// Distinct non-empty TT_Custom_Message values across active
// residents in the facility for a given mealtime (0=Breakfast,
// 1=Lunch, 2=Dinner). Powers the Tray Ticket Notes memo chevron
// dropdown so dietitians can pick a phrase another resident
// already has on file.
export function useTrayTicketMessages(facilityId: number, mealtime: number) {
  return useQuery({
    queryKey: ['patientTrayTicketMessages', facilityId, mealtime],
    queryFn: async (): Promise<string[]> => {
      const { data } = await apiClient.get<string[]>(
        API.patientTrayTicketMessages(facilityId, mealtime),
      );
      return data ?? [];
    },
    enabled: facilityId > 0,
    staleTime: 5 * 60 * 1000,
  });
}

// --- Interaction Logs ---

// Log type 5 = Patient interaction, type 6 = location (People page)
/**
 * Diet Fluctuations log history — every tblLog_Interaction row
 * for the patient whose Location is DietFluctuations. Backs the
 * Diet Fluctuations grid's History toolbar button. Server-side
 * the result is already sorted DESC by DateTimeOfLog + PKey.
 *
 * `enabled` is false by default so the query doesn't fire
 * until the user actually opens the History dialog — pass
 * `enabled: true` (or omit) to activate.
 */
export function useDietFluctuationHistoryLogs(facilityId: number, patientId: number, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...residentKeys.all, 'dietFlucHistory', facilityId, patientId] as const,
    queryFn: () =>
      fetchSafe<LogInteraction>(API.patientDietFluctuationHistory(facilityId, patientId)),
    enabled: (options.enabled ?? true) && facilityId > 0 && patientId > 0,
    staleTime: 60 * 1000,
  });
}

export function useInteractionLogs(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: residentKeys.interactionLogs(facilityId, patientId),
    queryFn: () =>
      fetchSafe<LogInteraction>(API.logInteractions(facilityId, 5, patientId)),
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

// ─── Global Meal Pattern / Adjustment interaction-log
//     hooks. Drive the Tools → History / Deleted Items
//     dialogs on the GlobalMealPatterns page. Shape mirrors
//     `useInteractionLogs` so the same `InteractionLogContent`
//     grid renders both.

export function useGlobalMPInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionGlobalMP', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionGlobalMP(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    // History dialogs never serve a cache: the rows are written by this
    // page moments earlier and by other sessions out of band, so a cached
    // list is wrong the instant anything is logged. See
    // `normalizeHistoryQuery` note above useAddInteractionLog.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useGlobalMPDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionGlobalMPDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionGlobalMPDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    // History dialogs never serve a cache: the rows are written by this
    // page moments earlier and by other sessions out of band, so a cached
    // list is wrong the instant anything is logged. See
    // `normalizeHistoryQuery` note above useAddInteractionLog.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useGlobalAdjInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionGlobalAdj', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionGlobalAdj(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    // History dialogs never serve a cache: the rows are written by this
    // page moments earlier and by other sessions out of band, so a cached
    // list is wrong the instant anything is logged. See
    // `normalizeHistoryQuery` note above useAddInteractionLog.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useGlobalAdjDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionGlobalAdjDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionGlobalAdjDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    // History dialogs never serve a cache: the rows are written by this
    // page moments earlier and by other sessions out of band, so a cached
    // list is wrong the instant anything is logged. See
    // `normalizeHistoryQuery` note above useAddInteractionLog.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Allergy Group interaction-log hooks. Drive the Allergy
//     Groups table's History → "Selected Allergy Group" and
//     "View Deleted Allergy Groups" dialogs. Same shape as the
//     Global hooks above so `InteractionLogContent` renders them
//     without changes.

export function useAllergyGroupInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionAllergyGroup', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionAllergyGroup(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    // Always refetch when the History dialog opens. These rows are written
    // by the page itself moments earlier (Add / Modify / View / Delete) and
    // by other sessions out of band, so a cached result is wrong the instant
    // anything is logged -- with a staleTime the dialog showed the pre-edit
    // list. Same reasoning as `useMenuCycleModifyLoad`.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Service Item interaction-log hooks. Drive the Service Items
//     table's History ribbon group — "Selected Item" and "View Deleted
//     Service Item" (ucDietech_ServiceItems.vb:475-503).
export function useServiceItemInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionServiceItem', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionServiceItem(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useServiceItemDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionServiceItemDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionServiceItemDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Global Meal Pattern Group interaction-log hooks. Drive the Meal
//     Pattern Global Groups table's History ribbon group
//     (ucDietech_MealPatternGlobalGroups.vb:254-282). The selected view
//     filters on FKey_Prd_Meal_Pattern_Global_Group alone, so it spans
//     both locations the tab writes to: GlobalMealPatternGroupAddMod
//     (29) for View / Add / Modify and GlobalMealPatternGroupDataGrid
//     (27) for Delete.
export function useGlobalMealPatternGroupInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionGlobalMealPatternGroup', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionGlobalMealPatternGroup(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useGlobalMealPatternGroupDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionGlobalMealPatternGroupDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionGlobalMealPatternGroupDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Global Adjustment Group interaction-log hooks. Drive the
//     Adjustment Global Groups table's History ribbon group
//     (ucDietech_AdjustmentGlobalGroups.vb:245-273). The selected view
//     filters on FKey_Prd_Adjustment_Global_Group alone, so it spans
//     both locations the tab writes to: GlobalAdjustmentGroupAddMod (30)
//     for View / Add / Modify and GlobalAdjustmentGroupDataGrid (28) for
//     Delete.
export function useGlobalAdjustmentGroupInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionGlobalAdjustmentGroup', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionGlobalAdjustmentGroup(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useGlobalAdjustmentGroupDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionGlobalAdjustmentGroupDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionGlobalAdjustmentGroupDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Dining Room interaction-log hooks. Drive the Dining Rooms table's
//     History ribbon group — "Selected Item" and the deleted view
//     (ucDietech_DiningRooms.vb:353-380). The selected view filters on
//     FKey_LayoutDiningRoom alone, so it spans both locations the tab
//     writes to: DiningRoom_AddMod (82) for View / Add / Modify and
//     DiningRoom_DataGrid (81) for Delete.
export function useDiningRoomInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionDiningRoom', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionDiningRoom(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useDiningRoomDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionDiningRoomDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionDiningRoomDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Tube Feed Product interaction-log hooks. Drive the Tube Feed
//     Products table's History ribbon group — "Selected Item" and the
//     deleted view (ucDietech_TubeFeedProducts.vb:364-392). The selected
//     view filters on FKey_Products alone, so it spans both locations
//     the tab writes to: TubeProduct_AddMod (90) for View / Add / Modify
//     and TubeProduct_DataGrid (89) for Delete.
export function useTubeFeedProductInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionTubeFeedProduct', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionTubeFeedProduct(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useTubeFeedProductDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionTubeFeedProductDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionTubeFeedProductDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Special Need interaction-log hooks. Drive the Special Needs
//     table's History ribbon group — "Selected Item" and "View Deleted
//     Special Needs" (ucDietech_Special_Needs.vb:473-500). The selected
//     view filters on FKey_Special_Needs alone, so it spans both
//     locations the tab writes to: SpecialNeedsAddMod (61) for
//     View / Add / Modify and SpecialNeedsDataGrid (60) for Delete and
//     Replace.
export function useSpecialNeedInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionSpecialNeed', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionSpecialNeed(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useSpecialNeedDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionSpecialNeedDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionSpecialNeedDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Portion interaction-log hook. Drives the Portions table's History
//     ribbon group — "Selected Item" (ucDietech_Portions.vb:110-122).
//     Filters on FKey_Prd_Portion alone; every row this table writes
//     lands at Portion_AddMod (78), View on open and Modify on save.
//     There is no deleted-portions view: the table has no Delete.
export function usePortionInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionPortion', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionPortion(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    // Always refetch when the History dialog opens — the page writes a
    // View row moments earlier, so a cached result is already stale.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Item Type interaction-log hooks. Drive the Item Types table's
//     History ribbon group — "Selected Item" and "View Deleted Item
//     Type" (ucDietech_ItemType.vb:719-760). The selected view filters
//     on FKey_Item_Prd_ItemType alone, so it spans both locations the
//     tab writes to: ItemTypeAddMod (54) for Add / View / Modify and
//     ItemTypeDataGrid (53) for Delete and Replace.
export function useItemTypeInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionItemType', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionItemType(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    // Always refetch when the History dialog opens — these rows are
    // written by the page itself moments earlier and by other sessions
    // out of band, so a cached result is wrong the instant anything is
    // logged. Same reasoning as `useAllergyGroupInteractionLogs`.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useItemTypeDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionItemTypeDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionItemTypeDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useDRIGroupInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionDRIGroup', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionDRIGroup(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    // History dialogs never serve a cache: the rows are written by this
    // page moments earlier and by other sessions out of band, so a cached
    // list is wrong the instant anything is logged. See
    // `normalizeHistoryQuery` note above useAddInteractionLog.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useDRIGroupDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionDRIGroupDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionDRIGroupDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    // History dialogs never serve a cache: the rows are written by this
    // page moments earlier and by other sessions out of band, so a cached
    // list is wrong the instant anything is logged. See
    // `normalizeHistoryQuery` note above useAddInteractionLog.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useAllergyGroupDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionAllergyGroupDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionAllergyGroupDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    // Always refetch when the History dialog opens. These rows are written
    // by the page itself moments earlier (Add / Modify / View / Delete) and
    // by other sessions out of band, so a cached result is wrong the instant
    // anything is logged -- with a staleTime the dialog showed the pre-edit
    // list. Same reasoning as `useMenuCycleModifyLoad`.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// ─── Food Group interaction-log hooks. Drive the Food Groups table's
//     History ribbon group — "Selected Food Group" and "View Deleted
//     Food Groups" (ucDietech_FoodGroups.vb:316-345). The selected view
//     filters on FKey_Prd_Food_Group alone, so it spans both locations
//     the tab writes to: FoodGroupAddMod (67) for Add and
//     FoodGroupDataGrid (50) for Modify / Delete.

/**
 * Always read on open, never from cache.
 *
 * WinForms builds `frmDietech_InteractionLog_DataGrid` from a fresh
 * `SqlDataAdapter.Fill` every time the ribbon button is pressed, so the history
 * always reflects the edit that was just made. A `staleTime` here breaks that:
 * rename a food group, reopen History, and React Query serves the pre-rename
 * list because the default `refetchOnMount` only refetches once data is STALE.
 *
 * `gcTime: 0` matters as much as `staleTime: 0` — without it the dialog paints
 * the cached rows first and swaps them a moment later, which reads as "the log
 * is missing" for exactly as long as anyone looks.
 */
export const HISTORY_QUERY_FRESHNESS = {
  staleTime: 0,
  gcTime: 0,
  refetchOnMount: 'always',
} as const;

/** Original name, kept so existing call sites keep reading naturally. */
const HISTORY_ALWAYS_FRESH = HISTORY_QUERY_FRESHNESS;

export function useFoodGroupInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionFoodGroup', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.foodGroupInteractionLogSelected(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    ...HISTORY_ALWAYS_FRESH,
  });
}

export function useFoodGroupDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionFoodGroupDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.foodGroupInteractionLogDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    ...HISTORY_ALWAYS_FRESH,
  });
}

// ─── Food Allergy interaction-log hooks. Drive the Food Allergies
//     table's History ribbon group — "Selected Item" and "View
//     Deleted Food Allergies" (ucDietech_Food_Allergies.vb:600-622).
//
// `staleTime: 0` + `refetchOnMount: 'always'`, unlike the 2-minute
// windows on the hooks above. An audit history is the one screen that
// must never answer from cache: it is opened precisely to check what just
// happened, and the rows it reads are appended by other users and other
// sessions out of band. A cached answer here is not a slightly-old list,
// it is a screen saying an action was never recorded.

export function useFoodAllergyInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionFoodAllergy', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionFoodAllergy(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useFoodAllergyDeletedInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionFoodAllergyDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionFoodAllergyDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

// Interaction log for a single Menu Cycle header — drives the
// Recycle Bin's "Selected Menu Cycle History" dialog. Same shape
// as the Global hooks above so the shared `InteractionLogContent`
// grid renders identically.
export function useMenuCycleInteractionLogs(facilityId: number, pKey: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionMenuHeader', facilityId, pKey] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionMenuHeader(facilityId, pKey)),
    enabled: enabled && facilityId > 0 && pKey > 0,
    // History dialogs never serve a cache: the rows are written by this
    // page moments earlier and by other sessions out of band, so a cached
    // list is wrong the instant anything is logged. See
    // `normalizeHistoryQuery` note above useAddInteractionLog.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

// Facility-wide deleted Menu Cycle history — drives Menu Cycles
// page's History → View Deleted dialog. Every Delete row in
// tblLog_Interaction whose FKey_Menu_Header IS NOT NULL. Same
// shape as `useMenuCycleInteractionLogs` so the shared
// `InteractionLogContent` grid renders identically.
export function useDeletedMenuCycleInteractionLogs(facilityId: number, enabled: boolean = true) {
  return useQuery({
    queryKey: [...residentKeys.all, 'logInteractionMenuHeaderDeleted', facilityId] as const,
    queryFn: () => fetchSafe<LogInteraction>(API.logInteractionMenuHeaderDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    // History dialogs never serve a cache: the rows are written by this
    // page moments earlier and by other sessions out of band, so a cached
    // list is wrong the instant anything is logged. See
    // `normalizeHistoryQuery` note above useAddInteractionLog.
    ...HISTORY_QUERY_FRESHNESS,
  });
}

export function useSystemLogs(facilityId: number, startDate: string, endDate: string) {
  return useQuery({
    queryKey: residentKeys.systemLogs(facilityId, startDate, endDate),
    queryFn: () =>
      fetchSafe<LogInteraction>(API.logInteractionSystem(facilityId, startDate, endDate)),
    enabled: facilityId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Facility-wide deleted-residents history — backs the
 * Manage Deleted page's "View Deleted Residents" toolbar
 * button. Pulls every Delete row in tblLog_Interaction whose
 * Location = ManageResidents for the current facility. Server
 * composes Notes as "LASTNAME, FIRSTNAME — original notes" so
 * the standard 6-column log grid carries the deleted-resident
 * identity in its Notes column.
 */
export function useDeletedResidentsLogs(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: residentKeys.deletedResidentsLogs(facilityId),
    queryFn: () =>
      fetchSafe<LogInteraction>(API.logInteractionDeletedResidents(facilityId)),
    enabled: enabled && facilityId > 0,
    staleTime: 30 * 1000,
  });
}

/**
 * Matches the query key of anything holding interaction-log rows. Two
 * naming conventions grew up side by side — the hooks in this file key on
 * `logInteraction<Entity>`, while the per-dialog queries and the composite
 * detail/entity reads key on `<entity>InteractionLog` / `interactionLogs`
 * — so both spellings are tested rather than one list of 20 keys that
 * the next history screen would forget to join.
 */
const INTERACTION_LOG_KEY = /loginteraction|interactionlog/i;

/**
 * Whether a React Query key holds interaction-log rows, and so has to be
 * invalidated when a new log row is written. Exported for the regression
 * test that pins it against the real key list — this bug is invisible
 * until someone opens a history screen and believes it.
 */
export function isInteractionLogQueryKey(queryKey: readonly unknown[]): boolean {
  return queryKey.some((part) => typeof part === 'string' && INTERACTION_LOG_KEY.test(part));
}

/**
 * Marks every interaction-log query stale.
 *
 * `useAddInteractionLog` does this for itself, but it only ever runs for
 * the log rows the CLIENT writes. The rows a controller writes inside its
 * own transaction — the Delete and Replace on Item Types, Special Needs
 * and Consistencies (see `SERVER_LOGS_DELETE` in Tables.tsx) — never pass
 * through that hook, so nothing invalidated the history caches and
 * "History (Deleted …)" kept serving a list that predated the delete the
 * operator had just done.
 *
 * Call this from any mutation whose log row is written server-side.
 */
export function invalidateInteractionLogs(queryClient: QueryClient): void {
  queryClient.invalidateQueries({
    predicate: (query) => isInteractionLogQueryKey(query.queryKey),
  });
}

export function useAddInteractionLog(facilityId: number) {
  const queryClient = useQueryClient();
  // Auto-attach the signed-in user's identity to every log
  // write so callers can hand us a minimal payload (location,
  // type, patient, notes) and the audit columns still
  // populate. Without this, log rows end up with empty
  // PMSec_User_* values and the Activity Log grid's User
  // Full Name / User Name columns render blank.
  const { userId, userName, userFirstName, userLastName } = useGlobalStore.getState();
  return useMutation({
    mutationFn: async (log: Partial<LogInteraction>) => {
      const enriched: Partial<LogInteraction> = {
        pmSec_User_PKey_User: userId || undefined,
        pmSec_User_UserName: userName || null,
        pmSec_User_FirstName: userFirstName || null,
        pmSec_User_LastName: userLastName || null,
        ...log, // explicit per-call overrides win
      };
      await apiClient.post(API.logInteractionAdd(facilityId), enriched);
    },
    onSuccess: () => {
      // Invalidate EVERY audit-history query, not just the resident one.
      //
      // This used to invalidate only `residentKeys.interactionLogs`, and
      // only when the payload named a patient. Every other history screen
      // — Food Allergies, Allergy Groups, DRI Groups, Food Groups, Diet
      // Types, Consistencies, Feeding Status, Special Devices, Global MP /
      // Adjustments, Menu Cycles — is keyed on its own entity and was
      // never invalidated by anything. Their rows went into the database
      // correctly (WinForms could see them) and the React dialog kept
      // serving its cached list, so a delete looked like it had written no
      // log at all. Reported against "View Deleted Food Allergies"
      // 2026-10-07.
      //
      // A write cannot know which histories it belongs to: a Delete on a
      // food allergy belongs to that allergy's history AND to the
      // facility-wide deleted list AND to the system activity log. So the
      // predicate invalidates them all. That is cheap — React Query
      // refetches only the queries currently MOUNTED, and at most one
      // history dialog is ever open; the rest are merely marked stale, so
      // the next time one opens it refetches instead of serving a cache
      // that predates this row.
      queryClient.invalidateQueries({
        predicate: (query) => isInteractionLogQueryKey(query.queryKey),
      });
    },
  });
}

// --- Mutations ---

/**
 * Response header a resident save (POST Patient/AddMod) uses for a message
 * to the user, such as the WinForms "Check Special Needs" reminder after the
 * special needs rules changed something. axios lower-cases header names.
 */
export const RESIDENT_NOTICE_HEADER = 'x-resident-notice';

/** The save notice from an AddMod response's headers, or null. */
export function residentNoticeFromHeaders(headers: unknown): string | null {
  if (!headers || typeof headers !== 'object') return null;
  const h = headers as { get?: (name: string) => unknown } & Record<string, unknown>;
  const raw = typeof h.get === 'function'
    ? h.get(RESIDENT_NOTICE_HEADER)
    : (h[RESIDENT_NOTICE_HEADER] ?? h['X-Resident-Notice']);
  const text = typeof raw === 'string' ? raw.trim() : '';
  return text.length > 0 ? text : null;
}

/** What useSavePatient resolves with. */
export interface SavedPatient {
  /** The server-assigned PKey (AddMod's bare int body), 0 when unreadable. */
  pKey_Patient: number;
  /** A message the page shows as a warning after the save (X-Resident-Notice), or null. */
  residentNotice: string | null;
}

/** The PKey from AddMod's body: a bare int (a numeric string is accepted too). */
function savedPKey(data: unknown): number {
  const n = typeof data === 'number' ? data : Number(typeof data === 'string' ? data.trim() : NaN);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function useSavePatient(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patient: Partial<Patient>): Promise<SavedPatient> => {
      const res = await apiClient.post(API.patientAddMod(facilityId), patient);
      // The body is the server-assigned PKey (int) for both Add and Modify;
      // a reminder for the user comes in a header so that body stays bare.
      return {
        pKey_Patient: savedPKey(res.data),
        residentNotice: residentNoticeFromHeaders(res.headers),
      };
    },
    // Every caller shows its own error UI (the stale / deleted dialog and
    // field marks in ResidentDetail, the wizard's message). Having an
    // onError here keeps App's global mutation toast from adding a raw
    // "stale" / "Missing row version" toast on top of it.
    onError: () => {},
    onSuccess: (saved, variables) => {
      // Optimistic list patch — merge the variables payload into
      // the row that matches on PKey. The server-side
      // `invalidateQueries` below will refetch and reconcile, but
      // the grid renders IMMEDIATELY off this in-place update so
      // an edit (Preferred Name / Room / etc.) shows up the moment
      // the operator lands back on the Residents grid. Without
      // this the grid keeps painting the pre-save cache for the
      // duration of the refetch round-trip.
      //
      // Add path: `variables.pKey_Patient` is 0 pre-save and the
      // server-returned int is the new row's PKey. Stamp both onto
      // the merged object so downstream renderers key correctly,
      // then prepend it to the list — the invalidation refetch
      // will replace the whole array with the server truth.
      const patchPKey = variables.pKey_Patient && variables.pKey_Patient > 0
        ? variables.pKey_Patient
        : saved.pKey_Patient;
      if (patchPKey > 0) {
        queryClient.setQueryData<Patient[]>(residentKeys.list(facilityId), (old) => {
          if (!old) return old;
          const idx = old.findIndex((p) => p.pKey_Patient === patchPKey);
          if (idx < 0) {
            // Add path — new row not yet in cache. Refetch will
            // fill in server-computed columns (DietOrder, etc.),
            // but stamp the visible name fields now so the grid
            // doesn't flash empty.
            return [
              { ...(variables as Patient), pKey_Patient: patchPKey },
              ...old,
            ];
          }
          const merged = { ...old[idx], ...variables, pKey_Patient: patchPKey } as Patient;
          const next = old.slice();
          next[idx] = merged;
          return next;
        });
      }

      queryClient.invalidateQueries({ queryKey: residentKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.flags(facilityId) });
      if (variables.pKey_Patient) {
        queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, variables.pKey_Patient) });
      }
    },
  });
}

/**
 * Payload shape for the new diet order history endpoint
 * (POST /api/Patient/DietOrder/AddMod/{facilityId}/{patientId}).
 * Each slot is nullable — both null and 0 are normalized to NULL
 * on the wrapper side. `pKey_Patient` is the target patient ID
 * (passed per-call so the same hook works for new-patient saves
 * where the PKey is only known after the patient INSERT
 * returns).
 */
export interface DietOrderAddModInput {
  pKey_Patient: number;
  fKey_Diet_Type1: number | null;
  fKey_Diet_Type2: number | null;
  fKey_Diet_Type3: number | null;
  fKey_Diet_Type4: number | null;
  fKey_Diet_Type5: number | null;
  fKey_Consist1: number | null;
  fKey_Consist2: number | null;
  fKey_Consist3: number | null;
  fKey_Consist4: number | null;
  fKey_Consist5: number | null;
}

/**
 * Server response shape from the diet order AddMod endpoint.
 * Carries the new tblDiet_Flucs PKey AND an optional toast
 * payload (currently used for the DRIGroup_AutoSelectBasedOnDiet
 * success / review notifications the legacy WinForms code
 * surfaces via alertMain.Show).
 *
 * `toastType` is "" when no toast should fire.
 */
export interface DietOrderAddModResult {
  pKey_Diet_Flucs: number;
  toastMessage: string;
  toastType: '' | 'success' | 'warning';
  /**
   * Set when the server's DRI auto-select wrote a new
   * FKey_RC_DRI_Group on tblPatient. Caller patches it onto
   * its local form state so the DRI chip in the patient banner
   * updates immediately.
   */
  newFKey_RC_DRI_Group: number | null;
}

/**
 * Insert a new tblDiet_Flucs row capturing the patient's current
 * 5 diet types + 5 consistencies. Server-side reuses the legacy
 * SQL_Diet_Flucs_Insert command via the wrapper's
 * modDietFlucs.InsertDietOrderHistory function — Change_Date is
 * stamped to NOW so the row becomes the patient's "current"
 * diet order on subsequent GETs.
 *
 * onSuccess invalidates both `dietHistory` (for the chips on
 * the patient banner + the In MC column source) and the patient
 * `detail` query (the GET joins through tblDiet_Flucs to derive
 * the denormalized DietOrder string and the per-slot FKs).
 */
/**
 * Payload shape for the Diet Fluctuations dialog Add endpoint.
 * Unlike DietOrderAddModInput, the user picks Change_Date /
 * EndDate / Note explicitly so they ride on the request.
 */
export interface DietFluctuationAddModInput {
  pKey_Patient: number;
  changeDate: string;          // ISO timestamp
  endDate: string | null;      // ISO timestamp or null
  note: string;
  fKey_Diet_Type1: number | null;
  fKey_Diet_Type2: number | null;
  fKey_Diet_Type3: number | null;
  fKey_Diet_Type4: number | null;
  fKey_Diet_Type5: number | null;
  fKey_Consist1: number | null;
  fKey_Consist2: number | null;
  fKey_Consist3: number | null;
  fKey_Consist4: number | null;
  fKey_Consist5: number | null;
}

/**
 * Insert a new tblDiet_Flucs row from the Diet Fluctuations
 * dialog (Add path only — Modify still uses useSaveDietFluc).
 *
 * Server-side, the wrapper's modDietFlucs.InsertDietFluctuation
 * decides the side-effect behavior based on where the user's
 * Change_Date falls in the patient's timeline:
 *   • Future / Backfill → INSERT only.
 *   • New "current" → full DietOrder flow (AutoNote,
 *     InteractionLog, DRIGroup_AutoSelectBasedOnDiet).
 *
 * The response shape is identical to the Diet Order endpoint —
 * see DietOrderAddModResult.
 *
 * onSuccess invalidates dietHistory (the grid), the patient
 * `detail` query (server-derived dietOrder string + DRI), and
 * dietFlucs.
 */
export function useSavePatientDietFluctuation(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DietFluctuationAddModInput): Promise<DietOrderAddModResult> => {
      const { pKey_Patient, ...body } = input;
      const { data } = await apiClient.post<DietOrderAddModResult>(
        API.patientDietFluctuationAddMod(facilityId, pKey_Patient),
        body,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: residentKeys.dietHistory(facilityId, variables.pKey_Patient) });
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, variables.pKey_Patient) });
      queryClient.invalidateQueries({ queryKey: residentKeys.dietFlucs(facilityId) });
    },
  });
}

/**
 * Payload shape for the Diet Fluctuations dialog Modify
 * endpoint. Identical to the Add payload plus the PKey of the
 * row being edited.
 */
export interface DietFluctuationModifyInput extends DietFluctuationAddModInput {
  pKey_Diet_Flucs: number;
}

/**
 * UPDATE an existing tblDiet_Flucs row from the Diet
 * Fluctuations dialog (Modify path).
 *
 * Server-side, the wrapper's modDietFlucs.UpdateDietFluctuation
 * runs the UPDATE then re-checks whether the edited row is the
 * patient's "current" diet (most recent Change_Date <= Now).
 * If yes, the same side-effect trio Case 3 of Add runs
 * (AutoNote, InteractionLog, DRIGroup_AutoSelectBasedOnDiet).
 * Otherwise the response carries just the PKey with empty
 * toast fields.
 *
 * onSuccess invalidates dietHistory (the grid + the header
 * badge memo), the patient `detail` query (server-derived
 * dietOrder + DRI), and dietFlucs.
 */
export function useModifyPatientDietFluctuation(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DietFluctuationModifyInput): Promise<DietOrderAddModResult> => {
      const { pKey_Patient, pKey_Diet_Flucs, ...body } = input;
      const { data } = await apiClient.post<DietOrderAddModResult>(
        API.patientDietFluctuationModify(facilityId, pKey_Patient, pKey_Diet_Flucs),
        body,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: residentKeys.dietHistory(facilityId, variables.pKey_Patient) });
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, variables.pKey_Patient) });
      queryClient.invalidateQueries({ queryKey: residentKeys.dietFlucs(facilityId) });
    },
  });
}

/**
 * Payload shape for the Diet Fluctuations Delete endpoint.
 * The route already carries the PKeys; the body is empty.
 */
export interface DietFluctuationDeleteInput {
  pKey_Patient: number;
  pKey_Diet_Flucs: number;
}

/**
 * DELETE an existing tblDiet_Flucs row.
 *
 * Server-side, the wrapper's
 * modDietFlucs.DeleteDietFluctuation pre-captures whether the
 * row is the patient's current diet. If yes, the
 * next-most-recent row becomes the new current and gets the
 * AutoNote / InteractionLog / DRI auto-select side effects.
 * The toast + NewFKey_RC_DRI_Group on the response then refer
 * to the NEW current row, not the deleted one.
 *
 * onSuccess invalidates the same query keys Add/Modify do, so
 * the grid + header chips refetch and the DRI chip mirrors
 * any newly-selected DRI group.
 */
export function useDeletePatientDietFluctuation(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DietFluctuationDeleteInput): Promise<DietOrderAddModResult> => {
      const { data } = await apiClient.delete<DietOrderAddModResult>(
        API.patientDietFluctuationDelete(facilityId, input.pKey_Patient, input.pKey_Diet_Flucs),
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: residentKeys.dietHistory(facilityId, variables.pKey_Patient) });
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, variables.pKey_Patient) });
      queryClient.invalidateQueries({ queryKey: residentKeys.dietFlucs(facilityId) });
    },
  });
}

/**
 * Apply a food-allergy delta to a patient. The React caller
 * diffs `existingPatient.fKey_Food_Allergy_*` against
 * `form.fKey_Food_Allergy_*` and ships the two lists of PKeys
 * (those removed from any slot, those added to any slot).
 * Server-side this routes through the legacy
 * PatientFoodAllergyUpdated which insert/removes the
 * patient's linked adjustments to match the new allergy set.
 *
 * onSuccess invalidates both the adjustments query (so the
 * Food Allergies tab's yellow/red color states recompute) and
 * the patient detail (in case any patient-level fields
 * derived from adjustments shift).
 */
export interface ApplyFoodAllergiesInput {
  pKey_Patient: number;
  remove: number[];
  add: number[];
}

export function useApplyPatientFoodAllergies(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ApplyFoodAllergiesInput): Promise<boolean> => {
      const { pKey_Patient, remove, add } = input;
      if (remove.length === 0 && add.length === 0) return false;
      const { data } = await apiClient.post<boolean>(
        API.patientFoodAllergiesApply(facilityId, pKey_Patient),
        { remove, add },
      );
      return data ?? false;
    },
    onSuccess: (_data, variables) => {
      // Adjustment query key shape mirrors residentKeys but
      // lives under adjustmentKeys in adjustmentApi.ts —
      // invalidating the whole `residents` root is too broad,
      // and a targeted invalidation needs adjustmentKeys.
      // Pull from adjustmentApi explicitly to keep this hook
      // self-contained.
      queryClient.invalidateQueries({
        queryKey: ['adjustment', 'byPatient', facilityId, variables.pKey_Patient],
      });
      // The slot colours and the meal pattern allergy banner: the apply adds
      // the resident's copies of the linked global adjustments.
      queryClient.invalidateQueries({ queryKey: foodAllergyKeys.warnings(facilityId, variables.pKey_Patient) });
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, variables.pKey_Patient) });
    },
  });
}

export function useSavePatientDietOrder(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DietOrderAddModInput): Promise<DietOrderAddModResult> => {
      const { pKey_Patient, ...body } = input;
      const { data } = await apiClient.post<DietOrderAddModResult>(
        API.patientDietOrderAddMod(facilityId, pKey_Patient),
        body,
      );
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: residentKeys.dietHistory(facilityId, variables.pKey_Patient) });
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, variables.pKey_Patient) });
      queryClient.invalidateQueries({ queryKey: residentKeys.dietFlucs(facilityId) });
      // The server checks / unchecks special needs that follow the diet
      // (tblSpecial_Needs AutoSelectOnDietMatch / DeSelectOnNoDietMatch).
      queryClient.invalidateQueries({ queryKey: ['patientSpecialNeeds', facilityId, variables.pKey_Patient] });
    },
  });
}

export function useDeletePatient(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patientId: number) => {
      await apiClient.delete(API.patientDelete(facilityId, patientId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: residentKeys.list(facilityId) });
      // Guests are patient rows too — deleting one through this
      // mutation must refresh the master-detail panel that
      // renders from `patientGuests`, otherwise the row sticks.
      queryClient.invalidateQueries({ queryKey: [...residentKeys.all, 'patientGuests', facilityId] });
    },
  });
}

/**
 * Format a Date as a local ISO-ish string (`YYYY-MM-DDTHH:mm:ss`)
 * with no trailing `Z`. .NET deserializes this as a DateTime
 * with `Kind=Unspecified`, which SQL Server stores verbatim —
 * keeping the user's local clock intact. Using `toISOString()`
 * instead converts to UTC and shifts the day for any non-UTC
 * client.
 */
function toLocalIsoString(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/**
 * Discharge a resident. Caller supplies the chosen date and
 * the "remove from tray line" decision from the prompt dialog;
 * server applies them to tblPatient + status history and (if
 * facility-gated) inserts a blank diet-flucs row.
 */
export function useDischargePatient(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ patientId, dischargeDate, removeFromTrayLine }: {
      patientId: number;
      dischargeDate: Date;
      removeFromTrayLine: boolean;
    }) => {
      await apiClient.post(API.patientDischarge(facilityId, patientId), {
        dischargeDate: toLocalIsoString(dischargeDate),
        removeFromTrayLine,
      });
      return patientId;
    },
    onSuccess: (patientId) => {
      queryClient.invalidateQueries({ queryKey: residentKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.flags(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.dietFlucs(facilityId) });
      // The dialog mutates Set_*/Tray_* + Discharge_Date on the
      // patient row; the detail query has the only cached copy
      // of those fields, so invalidate it too.
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.statusHistory(facilityId, patientId) });
    },
  });
}

/**
 * Readmit a discharged resident. WinForms always wipes the
 * tray line on readmit (no checkbox); the API does the same
 * server-side.
 */
export function useReadmitPatient(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ patientId, readmitDate }: {
      patientId: number;
      readmitDate: Date;
    }) => {
      await apiClient.post(API.patientReadmit(facilityId, patientId), {
        readmitDate: toLocalIsoString(readmitDate),
      });
      return patientId;
    },
    onSuccess: (patientId) => {
      queryClient.invalidateQueries({ queryKey: residentKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.flags(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.dietFlucs(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, patientId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.statusHistory(facilityId, patientId) });
    },
  });
}

/**
 * Guest → Quick Add. WinForms reference:
 * `frmDietech_Resident_GuestQuickAdd` + `AddGuest_Quick`.
 * Bulk-creates N guest patients under the focused parent
 * resident, each with the same B/L/D mealtime set and either
 * a one-time SpecificDate or a full recurrence (Daily /
 * Weekly). Server returns the list of new PKey_Patient
 * values; we invalidate the residents list so the new guests
 * appear in the grid.
 */
export interface QuickAddGuestsPayload {
  numberOfGuests: number;
  mealtime_B: boolean;
  mealtime_L: boolean;
  mealtime_D: boolean;
  scheduleType: 'OneTime' | 'Recurrence';
  // One-time only.
  specificDate?: string | null;
  // Recurrence — pattern: 0 = Daily, 1 = Weekly.
  recurrencePattern?: number | null;
  recurrenceOccursEveryNPattern?: number | null;
  recurrenceEveryWeekday?: boolean;
  recurrenceDay1?: boolean;
  recurrenceDay2?: boolean;
  recurrenceDay3?: boolean;
  recurrenceDay4?: boolean;
  recurrenceDay5?: boolean;
  recurrenceDay6?: boolean;
  recurrenceDay7?: boolean;
  recurrenceStarts?: string | null;
  recurrenceEndsBy?: string | null;
}

export function useQuickAddGuests(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ parentPatientId, payload }: {
      parentPatientId: number;
      payload: QuickAddGuestsPayload;
    }) => {
      const { data } = await apiClient.post<number[]>(
        API.patientQuickAddGuests(facilityId, parentPatientId),
        payload,
      );
      return data ?? [];
    },
    onSuccess: (_data, { parentPatientId }) => {
      queryClient.invalidateQueries({ queryKey: residentKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.flags(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.dietFlucs(facilityId) });
      // The Residents grid master-detail panel renders guests
      // from the `patientGuests` query (NOT `guests` — the keys
      // diverged historically). Invalidate the right one so the
      // focused parent row's expansion shows the new guests
      // after Save without a manual refresh.
      queryClient.invalidateQueries({ queryKey: [...residentKeys.all, 'patientGuests', facilityId] });
      queryClient.invalidateQueries({ queryKey: residentKeys.guestsByPatient(facilityId, parentPatientId) });
    },
  });
}

/**
 * Save (create or update) a single guest profile from the
 * dedicated GuestProfile page. WinForms ref:
 * `frmDietech_Guest_AddMod.SaveData`. PKey_Patient = 0 →
 * INSERT new guest; > 0 → UPDATE existing. Returns the saved
 * guest's PKey_Patient so the page can navigate from /new to
 * /:id after first save.
 */
export interface SaveGuestProfilePayload {
  pKey_Patient: number;                       // 0 = new
  fKey_Patient_Parent: number | null;         // insert only
  firstName: string | null;
  lastName: string | null;
  birthDate: string | null;
  allowSelectiveMenu: boolean;
  // Diet types + consistencies
  fKey_Diet_Type1: number | null;
  fKey_Diet_Type2: number | null;
  fKey_Diet_Type3: number | null;
  fKey_Diet_Type4: number | null;
  fKey_Diet_Type5: number | null;
  fKey_Consist1: number | null;
  fKey_Consist2: number | null;
  fKey_Consist3: number | null;
  fKey_Consist4: number | null;
  fKey_Consist5: number | null;
  // Food allergies 1-5
  fKey_Food_Allergy_1: number | null;
  fKey_Food_Allergy_2: number | null;
  fKey_Food_Allergy_3: number | null;
  fKey_Food_Allergy_4: number | null;
  fKey_Food_Allergy_5: number | null;
  // Special Devices 1-5 + per-device mealtime mask
  fKey_Spec_Devices_1: number | null;
  fKey_Spec_Devices_2: number | null;
  fKey_Spec_Devices_3: number | null;
  fKey_Spec_Devices_4: number | null;
  fKey_Spec_Devices_5: number | null;
  fKey_Spec_Devices_1_Time: number | null;
  fKey_Spec_Devices_2_Time: number | null;
  fKey_Spec_Devices_3_Time: number | null;
  fKey_Spec_Devices_4_Time: number | null;
  fKey_Spec_Devices_5_Time: number | null;
  // Selective Menu (`SelectiveMenuTab`)
  staticSelectiveMenu: boolean;
  sM_Notes_B: string | null;
  sM_Notes_L: string | null;
  sM_Notes_D: string | null;
  // Tray Ticket Notes (`TrayTicketNotesPanel`)
  tT_Note_B: string | null;
  tT_Note_L: string | null;
  tT_Note_D: string | null;
  tT_Custom_Message: string | null;
  tT_Custom_Message_L: string | null;
  tT_Custom_Message_D: string | null;
  b_Location: string | null;
  l_Location: string | null;
  d_Location: string | null;
  // Schedule
  mealtime_B: boolean;
  mealtime_L: boolean;
  mealtime_D: boolean;
  scheduleType: 'OneTime' | 'Recurrence';
  specificDate: string | null;
  recurrencePattern: number | null;
  recurrenceOccursEveryNPattern: number | null;
  recurrenceEveryWeekday: boolean;
  recurrenceDay1: boolean;
  recurrenceDay2: boolean;
  recurrenceDay3: boolean;
  recurrenceDay4: boolean;
  recurrenceDay5: boolean;
  recurrenceDay6: boolean;
  recurrenceDay7: boolean;
  recurrenceStarts: string | null;
  recurrenceEndsBy: string | null;
  // Change flags (drive side effects: diet flucs insert,
  // schedule UPSERT, audit log notes).
  dietOrderChanged: boolean;
  scheduleChanged: boolean;
}

export function useSaveGuestProfile(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: SaveGuestProfilePayload) => {
      const { data } = await apiClient.post<number>(
        API.patientSaveGuestProfile(facilityId),
        payload,
      );
      return data ?? 0;
    },
    onSuccess: (pKey) => {
      // Refresh the things that surface guests: main residents
      // list, the per-parent master-detail guest list, and diet
      // flucs (in case a new flucs row was inserted).
      queryClient.invalidateQueries({ queryKey: residentKeys.list(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.dietFlucs(facilityId) });
      queryClient.invalidateQueries({ queryKey: [...residentKeys.all, 'patientGuests', facilityId] });
      if (pKey > 0) {
        queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, pKey) });
      }
    },
  });
}

// --- Deleted Patients ---

/** `enabled` false skips the request: a role without Managed Residents gets a 403 (and a toast). */
export function useDeletedPatients(facilityId: number, enabled = true) {
  return useQuery({
    queryKey: residentKeys.deleted(facilityId),
    queryFn: () => fetchSafe<Patient>(API.patientDeleted(facilityId)),
    enabled: enabled && facilityId > 0,
    staleTime: 30 * 1000,
  });
}

/**
 * Slim variant backing the Manage Deleted grid — returns just
 * the ~10 columns the grid renders. Use this when you only need
 * grid data; use `useDeletedPatients` if you need the full
 * TblPatient blob (e.g. some downstream action requiring image,
 * diet history, allergies, etc.).
 */
export function useDeletedPatientsSummary(facilityId: number) {
  return useQuery({
    queryKey: residentKeys.deletedSummary(facilityId),
    queryFn: () => fetchSafe<PatientDeletedSummary>(API.patientDeletedSummary(facilityId)),
    enabled: facilityId > 0,
    staleTime: 30 * 1000,
  });
}

export function useRestorePatient(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patientId: number) => {
      await apiClient.post(API.patientRestore(facilityId, patientId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: residentKeys.deleted(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.deletedSummary(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.list(facilityId) });
    },
  });
}

export function usePermanentDeletePatient(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patientId: number) => {
      await apiClient.delete(API.patientPermanentDelete(facilityId, patientId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: residentKeys.deleted(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.deletedSummary(facilityId) });
    },
  });
}

export function usePermanentDeleteAllPatients(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(API.patientPermanentDeleteAll(facilityId));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: residentKeys.deleted(facilityId) });
      queryClient.invalidateQueries({ queryKey: residentKeys.deletedSummary(facilityId) });
    },
  });
}

// --- Patient Attendance ---

export function usePatientAttendance(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: residentKeys.attendance(facilityId, patientId),
    queryFn: () => fetchSafe<PatientAttendance>(API.patientAttendance(facilityId, patientId)),
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSavePatientAttendance(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (attendance: PatientAttendance[]) => {
      await apiClient.post(API.patientSaveAttendance(facilityId), attendance);
    },
    onSuccess: (_data, variables) => {
      const patientId = variables[0]?.fKey_Patient;
      if (patientId) {
        queryClient.invalidateQueries({ queryKey: residentKeys.attendance(facilityId, patientId) });
      }
    },
  });
}

// --- Patient Attendance Extended (with nourishments + SM) ---

export function usePatientAttendanceExtended(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: residentKeys.attendanceExtended(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<PatientAttendanceExtended>(
        API.patientAttendanceExtended(facilityId, patientId),
      );
      return data;
    },
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSavePatientAttendanceExtended(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: PatientAttendanceExtended) => {
      await apiClient.post(API.patientSaveAttendanceExtended(facilityId), data);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: residentKeys.attendanceExtended(facilityId, variables.fKey_Patient),
      });
      // Also invalidate the simple attendance query for consistency
      queryClient.invalidateQueries({
        queryKey: residentKeys.attendance(facilityId, variables.fKey_Patient),
      });
      // Writes tblPatient, which changes its row version: refetch the
      // resident so an open Resident page takes the new one (a stale one
      // makes its next save fail with 409).
      queryClient.invalidateQueries({
        queryKey: residentKeys.detail(facilityId, variables.fKey_Patient),
      });
      // Attendance changes could invalidate the future-SM flag map
      // (e.g. checking an SM day back on doesn't touch the DB but
      // the user may next look at the flag and expect it fresh).
      // Cheap invalidation — the query will refetch on next mount.
      queryClient.invalidateQueries({
        queryKey: [...residentKeys.all, 'futureSMDays', facilityId, variables.fKey_Patient],
      });
    },
  });
}

// --- Future Selective Menu deletion (per-day flags) ---

/**
 * Server-side map of `mealtime → bool[7]` (Mon=0..Sun=6) indicating
 * whether the patient has any future `tblPrd_Selective_Menu` rows
 * queued for that mealtime × day-of-week. Drives the "SM will be
 * deleted" indicator + Delete button in AttendanceEditor. Mirrors
 * WinForms `FutureSMEntered` at ucDietech_Resident_AttendanceNew.vb.
 * Keys arrive as strings from JSON — cast back to numbers when
 * indexing.
 */
export type FutureSelectiveMenuDaysMap = Record<string, boolean[]>;

export function usePatientFutureSelectiveMenuDays(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: [...residentKeys.all, 'futureSMDays', facilityId, patientId] as const,
    queryFn: async () => {
      const { data } = await apiClient.get<FutureSelectiveMenuDaysMap>(
        API.patientFutureSelectiveMenuDays(facilityId, patientId),
      );
      return data;
    },
    enabled: facilityId > 0 && patientId > 0,
    // Kept short so the map refreshes when the user returns to the
    // tab after making changes elsewhere (e.g. the Selective Menu
    // editor purging entries independently).
    staleTime: 30 * 1000,
  });
}

export function useDeleteFutureSelectiveMenuDays(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      patientId,
      mealtime,
      mondayIndexedDays,
    }: {
      patientId: number;
      mealtime: number;
      mondayIndexedDays: number[];
    }) => {
      const { data } = await apiClient.post<number>(
        API.patientDeleteFutureSelectiveMenuDays(facilityId, patientId, mealtime),
        mondayIndexedDays,
      );
      return data;
    },
    onSuccess: (_deleted, variables) => {
      // The future-SM map is now stale — force a refetch so the
      // Attendance editor's flags reflect the DB.
      queryClient.invalidateQueries({
        queryKey: [...residentKeys.all, 'futureSMDays', facilityId, variables.patientId],
      });
    },
  });
}

// --- Patient Tray Line ---

export function usePatientTrayLine(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: residentKeys.trayLine(facilityId, patientId),
    queryFn: () => fetchSafe<PatientTrayLine>(API.patientTrayLine(facilityId, patientId)),
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSavePatientTrayLine(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (trayLines: PatientTrayLine[]) => {
      await apiClient.post(API.patientSaveTrayLine(facilityId, patientId), trayLines);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: residentKeys.trayLine(facilityId, patientId) });
      // Writes tblPatient, which changes its row version: refetch the
      // resident so an open Resident page takes the new one (a stale one
      // makes its next save fail with 409).
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, patientId) });
    },
  });
}

// ─── New tray-line envelope hooks ────────────────────────────
// Combined GET (defaults + overrides) + two save mutations.
// These back the rebuilt Tray Line patient tab.

export function usePatientTrayLineEnvelope(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: residentKeys.trayLineEnvelope(facilityId, patientId),
    queryFn: async () => {
      // `fetchSafe` is shaped for list endpoints (always returns
      // T[]); the envelope is a single object so call apiClient
      // directly.
      const { data } = await apiClient.get<PatientTrayLineEnvelope>(
        API.patientTrayLineEnvelope(facilityId, patientId),
      );
      return data;
    },
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 60 * 1000,
  });
}

export function useSavePatientTrayLineDefaults(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (defaults: PatientTrayLineDefaults) => {
      await apiClient.put(API.patientTrayLineDefaults(facilityId, patientId), defaults);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: residentKeys.trayLineEnvelope(facilityId, patientId) });
      // Writes tblPatient, which changes its row version: refetch the
      // resident so an open Resident page takes the new one (a stale one
      // makes its next save fail with 409).
      queryClient.invalidateQueries({ queryKey: residentKeys.detail(facilityId, patientId) });
      // Tray_B/L/D drive the "not assigned to a tray line" flag (#2080).
      queryClient.invalidateQueries({ queryKey: residentKeys.flags(facilityId) });
    },
  });
}

export function useSavePatientTrayLineOverrides(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    // Returns the saved rows with server-assigned PKeys so the
    // client can refresh its in-memory model without an extra
    // round-trip (still invalidates the envelope query below
    // for safety).
    mutationFn: async (overrides: PatientTrayLineOverrideRow[]) => {
      const { data } = await apiClient.put<PatientTrayLineOverrideRow[]>(
        API.patientTrayLineOverrides(facilityId, patientId),
        overrides,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: residentKeys.trayLineEnvelope(facilityId, patientId) });
      // Other residents' Tray Line tabs mark a slot this resident's override holds.
      queryClient.invalidateQueries({ queryKey: trayLineKeys.overrides(facilityId) });
    },
  });
}

/**
 * The resident's tray line as stored, fetched fresh (the Tray Line tab's
 * cache takes it too). The post-save Assign prompt reads it: the server's
 * unit auto-assign may have filled meals during the save.
 */
export async function fetchPatientTrayLineEnvelope(
  queryClient: ReturnType<typeof useQueryClient>,
  facilityId: number,
  patientId: number,
): Promise<PatientTrayLineEnvelope> {
  return queryClient.fetchQuery({
    queryKey: residentKeys.trayLineEnvelope(facilityId, patientId),
    queryFn: async () => {
      const { data } = await apiClient.get<PatientTrayLineEnvelope>(
        API.patientTrayLineEnvelope(facilityId, patientId),
      );
      return data;
    },
    staleTime: 0,
  });
}

// Batch save for the Assign Tray Line dialog. Posts every
// resident whose Set/Tray/Location moved in one transaction.
// Invalidates the resident list AND every patient's tray-line
// envelope cache so the page picks up server-side values
// immediately.
export function useSavePatientTrayLineAssignments(facilityId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (assignments: PatientTrayLineAssignmentRow[]) => {
      await apiClient.put(API.patientTrayLineAssignments(facilityId), assignments);
      return assignments;
    },
    onSuccess: (_data, variables) => {
      // The reorder can touch many residents; invalidate the
      // shared "patients for tray line" query AND the
      // facility-wide resident list.
      queryClient.invalidateQueries({ queryKey: residentKeys.list(facilityId) });
      // The resident flags carry the "not assigned to a tray line"
      // warning (Overview Tray Line tile, grid flag). Without this the
      // warning stayed up after an assignment until the flags query
      // went stale on its own (#2080).
      queryClient.invalidateQueries({ queryKey: residentKeys.flags(facilityId) });
      queryClient.invalidateQueries({
        predicate: (q) => Array.isArray(q.queryKey)
          && q.queryKey.includes('patients')
          && q.queryKey.includes(facilityId),
      });
      // A new location joins the Assign dialog's Location lists.
      queryClient.invalidateQueries({ queryKey: trayLineKeys.locations(facilityId) });
      // Every saved resident's Tray Line tab envelope (defaults
      // + overrides) — the standard/override grid otherwise
      // serves cached values and the user has to navigate away
      // and back to see the new Set/Tray/Location. Detail is
      // also invalidated so the Tray Ticket Notes panel
      // re-reads the new B_Location/L_Location/D_Location.
      for (const row of variables) {
        queryClient.invalidateQueries({
          queryKey: residentKeys.trayLineEnvelope(facilityId, row.pKey_Patient),
        });
        queryClient.invalidateQueries({
          queryKey: residentKeys.detail(facilityId, row.pKey_Patient),
        });
      }
    },
  });
}

// --- Patient Location Exceptions ---

export interface LocationException {
  pKey_Patient_Location_Excpt: number;
  fKey_Patient: number;
  location: string;
  day_1: boolean;
  day_2: boolean;
  day_3: boolean;
  day_4: boolean;
  day_5: boolean;
  day_6: boolean;
  day_7: boolean;
  mT_B: boolean;
  mT_L: boolean;
  mT_D: boolean;
}

export function usePatientLocationExceptions(facilityId: number, patientId: number) {
  return useQuery({
    queryKey: [...residentKeys.all, 'locationExceptions', facilityId, patientId] as const,
    queryFn: async (): Promise<LocationException[]> => {
      const { data } = await apiClient.get<LocationException[]>(
        API.patientLocationExceptions(facilityId, patientId),
      );
      return data ?? [];
    },
    enabled: facilityId > 0 && patientId > 0,
    staleTime: 2 * 60 * 1000,
  });
}

export function useSavePatientLocationExceptions(facilityId: number, patientId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (exceptions: LocationException[]) => {
      await apiClient.post(
        API.patientSaveLocationExceptions(facilityId, patientId),
        exceptions,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [...residentKeys.all, 'locationExceptions', facilityId, patientId],
      });
    },
  });
}
